<script setup lang="ts">
import { computed } from 'vue'
import DOMPurify from 'dompurify'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import Skeleton from '@/components/Skeleton.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'

const props = defineProps({
	title: {
		type: String,
		default: '',
	},
	region: {
		type: String,
		default: '',
	},
	isOfficial: {
		type: Boolean,
		default: false,
	},
	officialText: {
		type: String,
		default: '',
	},
	buttonText: {
		type: String,
		default: '',
	},
	averageDiscount: {
		type: Number,
		default: 0,
	},
	discountText: {
		type: String,
		default: '',
	},
	buttonLink: {
		type: String,
		default: '',
	},
	isTablet: {
		type: Boolean,
		default: false,
	},
	isLoading: {
		type: Boolean,
		default: true,
	},
})

const currentDiscountText = computed(() => {
	const rawHtml = props.discountText.replace(
		'{discount}',
		`<span class="typography-l1-emp text-o-500">${props.averageDiscount}%</span>`,
	)
	return DOMPurify.sanitize(rawHtml)
})

function handleButtonClick() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariBrand,
		button_name: 'Visit Merchant site',
		event_extra: props.title,
	})
}
</script>

<template>
	<div
		class="flex flex-col rounded-xl border border-solid border-[#CCCBCB] p-16px mdp:p-20px"
	>
		<div class="mb-16px flex items-start justify-between gap-8px break-all">
			<div class="flex flex-col items-start text-left">
				<div class="mb-4px">
					<Skeleton v-if="isLoading" class="h-24px w-[120px]" />
					<h5 v-else class="typography-h5-str text-n-950">{{ title }}</h5>
				</div>
				<Skeleton v-if="isLoading" class="h-20px w-48px rounded-full" />
				<p
					v-else
					class="typography-c2-reg select-none rounded-full bg-n-50 px-[6px] py-[2px] text-n-800"
				>
					{{ region }}
				</p>
			</div>
			<Skeleton v-if="isLoading" class="h-20px w-72px rounded-full" />
			<span
				v-if="!isLoading && isOfficial"
				class="typography-c1-reg shrink-0 select-none rounded-full bg-t-75 px-12px py-4px text-t-600"
			>
				{{ officialText }}
			</span>
		</div>

		<div class="mb-16px text-left">
			<Skeleton v-if="isLoading" class="h-[17px] w-96px" />
			<template v-else>
				<p
					v-if="Boolean(averageDiscount)"
					class="typography-l2-reg text-n-950"
					v-html="currentDiscountText"
				></p>
			</template>
		</div>
		<div class="mt-auto inline-block mdl:self-end mdl:text-right">
			<Skeleton
				v-if="isLoading"
				class="h-48px w-full rounded-full mdl:w-96px"
			/>
			<a v-else :href="buttonLink" target="_blank">
				<BaseButton
					variant="primary-inverse"
					size="medium"
					class="mdl:self-end"
					:isFull="!isTablet"
					@click="handleButtonClick"
				>
					{{ buttonText }}
				</BaseButton>
			</a>
		</div>
	</div>
</template>
